import cx from 'classnames';
import { t } from 'i18next';
import _ from 'lodash';
import { Divider, SubMenu, MenuItem } from 'rc-menu';
import { FC, ReactElement } from 'react';
import { handleRCTitleMenuOnClick } from '../../libs/history';
import { DropdownTitle } from './dropdownTitle';
import { IEventBracketDetails, IEventBracketTeamDetails } from '../../../types/eventBracketTypes';
import WowIcon from '../../scripts/wow/components/WowIcon';

// Custom placements that disable vertical auto-adjustment
// This allows menus to extend beyond viewport and be scrolled to, like rc-menu 8.x behavior
// Using htmlRegion: 'scroll' makes rc-trigger check against scroll height instead of viewport
const noAutoAdjustOverflow = {
  adjustX: true,
  adjustY: false
};

const createPlacement = (points: [string, string]) => ({
  points,
  overflow: noAutoAdjustOverflow,
  htmlRegion: 'scroll' as const
});

export const menuBuiltinPlacements = {
  topLeft: createPlacement(['bl', 'tl']),
  topRight: createPlacement(['br', 'tr']),
  bottomLeft: createPlacement(['tl', 'bl']),
  bottomRight: createPlacement(['tr', 'br']),
  leftTop: createPlacement(['tr', 'tl']),
  leftBottom: createPlacement(['br', 'bl']),
  rightTop: createPlacement(['tl', 'tr']),
  rightBottom: createPlacement(['bl', 'br'])
};

export const enum EMenuEntry {
  ITEM = 'item',
  DIVIDER = 'divider'
}

type TMenuEntryDivider = {
  type: EMenuEntry.DIVIDER;
  isHidden?: boolean;
};

type TChildrenGenerator = Function; // (entry: TMenuEntry) => TMenuEntry[];

export type TMenuEntryItem = {
  type: EMenuEntry.ITEM;
  title: any;
  titleClass?: string;
  link?: string;
  key?: string;
  className?: string;
  children?: TMenuEntry[] | TChildrenGenerator;
  customIcon?: string;
  customIconComponent?: ReactElement;
  iconClassName?: string; // TODO: remove and only use customIcon?
  wowIcon?: string | string[]; // TODO: remove and only use custom Icon?
  isNew?: boolean;
  isBeta?: boolean;
  isLive?: boolean;
  isPremium?: boolean;
  isHidden?: boolean;
  wowIcons?: string[];
  postIconClass?: string; // class to put on a suffix after title
  onClick?: () => any;
  disabled?: boolean;
  active?: boolean;

  // these can bse used if you need more flexibility than the provided icons.
  PreIcon?: FC;
  PostIcon?: FC;
};

export type TMenuEntry = TMenuEntryDivider | TMenuEntryItem;
export function buildMenus(prefix: string, menus: TMenuEntry[], params?: any) {
  const { handleSubMenuClick = handleRCTitleMenuOnClick } = params || {};
  const items: any[] = [];
  menus.forEach((menu: TMenuEntry, index: number) => {
    if (!menu.isHidden) {
      if (menu.type === EMenuEntry.DIVIDER) {
        items.push(<Divider key={index} />);
      } else if (menu.type === EMenuEntry.ITEM) {
        let titleData: any;

        if (typeof menu.title === 'string') {
          titleData = t(menu.title);
        } else if (typeof menu.title === 'function') {
          titleData = menu.title(menu);
        } else {
          titleData = menu.title;
        }

        const titleLine: any = (
          <>
            {menu.iconClassName && <div className={cx(menu.iconClassName, 'slds-m-right--x-small')} />}
            {menu.wowIcon && (
              <>
                {([] as string[]).concat(menu.wowIcon).map((icon, iconIndex) => {
                  return <WowIcon lazy icon={icon} size="xx-small" key={iconIndex} className="slds-m-right--x-small" />;
                })}
              </>
            )}
            {menu.customIcon && (
              <span className="slds-avatar slds-avatar--xx-small rio-border--light rio-shadow--small slds-m-right--x-small">
                <img loading="lazy" src={menu.customIcon} />
              </span>
            )}
            {menu.customIconComponent && (
              <span className="slds-avatar slds-avatar--xx-small rio-border--light rio-shadow--small slds-m-right--x-small">
                {menu.customIconComponent}
              </span>
            )}

            {menu.PreIcon && <menu.PreIcon />}
            <span className={menu.titleClass}>{titleData}</span>
            {menu.PostIcon && <menu.PostIcon />}

            {menu.postIconClass && <i className={menu.postIconClass} />}
            {menu.isNew && <span className="slds-m-left--x-small new-feature-indicator">{t('nav:suffixes.new')}</span>}
            {menu.isBeta && (
              <span className="slds-m-left--x-small new-feature-indicator">{t('nav:suffixes.beta')}</span>
            )}
            {menu.isLive && <span className="slds-m-left--x-small rwf-live-indicator">{t('nav:suffixes.live')}</span>}
            {menu.isPremium && (
              <span className="slds-m-left--x-small new-feature-indicator">{t('nav:suffixes.premium')}</span>
            )}
          </>
        );

        if (menu.children) {
          let childMenus: any = null;
          if (_.isArray(menu.children) && menu.children.length > 0) {
            childMenus = buildMenus(`${prefix}:child${index}`, menu.children, params);
          } else if (typeof menu.children === 'function') {
            childMenus = buildMenus(`${prefix}:child${index}`, menu.children(menu), params);
          }

          items.push(
            <SubMenu
              key={`${prefix}:${index}`}
              disabled={menu.disabled}
              onTitleClick={(event: any) => {
                event.domEvent.preventDefault();
                event.domEvent.stopPropagation();
                handleSubMenuClick(menu.link, event);
                if (menu.onClick) {
                  menu.onClick();
                }
              }}
              title={<span className={menu.className}>{titleLine}</span>}
            >
              {childMenus}
            </SubMenu>
          );
        } else {
          items.push(
            <MenuItem
              key={menu.key || menu.link}
              disabled={menu.disabled}
              onClick={menu.onClick}
              className={{
                'rc-menu-item-active': menu.active
              }}
            >
              <span className={menu.className}>{titleLine}</span>
            </MenuItem>
          );
        }
      }
    }
  });
  return items;
}
export const getManageEventBracketMenus = (config?: { disabledOptions: boolean }): TMenuEntry[] => {
  return [
    {
      type: EMenuEntry.ITEM,
      title: <i className="fa fa-ellipsis-vertical" />,
      children: [
        {
          type: EMenuEntry.ITEM,
          iconClassName: 'fa fa-copy',
          className: '',
          title: t('settings:events-management.buttons.copy-bracket'),
          link: 'copy',
          disabled: !!config?.disabledOptions
        },
        {
          type: EMenuEntry.DIVIDER
        },
        {
          type: EMenuEntry.ITEM,
          iconClassName: 'fa fa-trash',
          className: 'text-error',
          title: t('settings:events-management.buttons.delete-bracket'),
          link: 'delete',
          disabled: !!config?.disabledOptions
        }
      ]
    }
  ];
};
type TGetManageEventBracketTeamMenusParams = {
  eventBracketDetails: IEventBracketDetails;
  eventBracketTeam: IEventBracketTeamDetails;
  disabledOptions?: boolean;
};

export const getManageEventBracketTeamMenus = (params: TGetManageEventBracketTeamMenusParams): TMenuEntry[] => {
  const { eventBracketDetails, eventBracketTeam, disabledOptions } = params;
  return [
    {
      type: EMenuEntry.ITEM,
      title: <i className="fa fa-ellipsis-vertical" />,
      children: [
        ...(eventBracketDetails.requireTeamCheckin && eventBracketTeam.checkedInAt
          ? [
              {
                type: EMenuEntry.ITEM,
                iconClassName: 'fa fa-person-from-portal',
                className: '',
                title: 'Force Check Out',
                link: 'check-out',
                disabled: disabledOptions
              }
            ]
          : []),
        ...(eventBracketDetails.requireTeamCheckin && !eventBracketTeam.checkedInAt
          ? [
              {
                type: EMenuEntry.ITEM,
                iconClassName: 'fa fa-person-to-portal',
                className: '',
                title: 'Force Check In',
                link: 'check-in',
                disabled: disabledOptions
              }
            ]
          : []),
        {
          type: EMenuEntry.ITEM,
          iconClassName: 'fa fa-edit',
          title: t('settings:events-management.buttons.edit'),
          link: 'team-edit'
        },
        {
          type: EMenuEntry.ITEM,
          disabled: disabledOptions,
          iconClassName: disabledOptions ? 'fa fa-lock' : 'fa fa-trash',
          className: disabledOptions ? '' : 'text-error',
          title: t('settings:events-management.buttons.delete-team-from-bracket'),
          link: 'delete'
        }
      ]
    }
  ];
};

export const getManageEventBracketMatchMenus = (options: {
  areMatchesSeeded: boolean;
  isBracketStarted: boolean;
  isEveryTeamHasSeed: boolean;
  areChangesAllowed: boolean;
  canCreateMatches: boolean;
}): TMenuEntry[] => {
  return [
    {
      type: EMenuEntry.ITEM,
      title: (
        <DropdownTitle
          title={
            <span>
              <i className="fa fa-file-spreadsheet slds-m-right--x-small" />
              Manage Matches
            </span>
          }
        />
      ),
      children: [
        {
          type: EMenuEntry.ITEM,
          iconClassName: 'fa fa-square-plus',
          className: '',
          title: 'Create & Seed Matches',
          link: 'create',
          disabled: !options.areChangesAllowed || !options.canCreateMatches
        },
        {
          type: EMenuEntry.ITEM,
          iconClassName: 'fa fa-random',
          className: '',
          title: t('settings:events-management.buttons.seed-matches'),
          link: 'seed',
          disabled:
            options.areMatchesSeeded ||
            options.isBracketStarted ||
            !options.isEveryTeamHasSeed ||
            !options.areChangesAllowed
        },
        {
          type: EMenuEntry.DIVIDER
        },
        {
          type: EMenuEntry.ITEM,
          iconClassName: 'fa fa-undo',
          className: '',
          title: t('settings:events-management.buttons.reset-matches'),
          link: 'reset',
          disabled: !options.areMatchesSeeded || options.isBracketStarted || !options.areChangesAllowed
        },
        {
          type: EMenuEntry.DIVIDER
        },
        {
          type: EMenuEntry.ITEM,
          iconClassName: 'fa fa-trash text-danger',
          className: '',
          title: t('settings:events-management.buttons.delete-matches'),
          link: 'delete',
          disabled: options.isBracketStarted || !options.areChangesAllowed
        }
      ]
    }
  ];
};

export const getManageEventBracketTeamsMenus = (options: {
  isTeamsNotFullCount: boolean;
  doesTeamExist: boolean;
  isBracketStarted: boolean;
  isEveryTeamSeeded: boolean;
  areChangesAllowed: boolean;
  isSomeTeamsUncheckedIn: boolean;
  requireTeamCheckin: boolean;
}): TMenuEntry[] => {
  return [
    {
      type: EMenuEntry.ITEM,
      title: (
        <DropdownTitle
          title={
            <span>
              <i className="fa fa-people-group slds-m-right--x-small" />
              Manage Teams
            </span>
          }
        />
      ),
      children: [
        {
          type: EMenuEntry.ITEM,
          iconClassName: 'fa fa-plus',
          className: '',
          title: t('settings:events-management.buttons.add-eligible-teams'),
          link: 'add-eligible',
          disabled: !options.isTeamsNotFullCount || options.isBracketStarted || !options.areChangesAllowed
        },
        {
          type: EMenuEntry.ITEM,
          iconClassName: 'fa fa-edit',
          className: '',
          title: t('settings:events-management.buttons.batch-update-teams'),
          link: 'batch-update-teams',
          disabled: !options.areChangesAllowed
        },
        {
          type: EMenuEntry.DIVIDER
        },
        {
          type: EMenuEntry.ITEM,
          iconClassName: 'fa fa-file-export',
          className: '',
          title: 'Export Team Seeding CSV',
          link: 'export-seed-csv',
          disabled: options.isBracketStarted || !options.areChangesAllowed
        },
        {
          type: EMenuEntry.ITEM,
          iconClassName: 'fa fa-file-import',
          className: '',
          title: 'Import Team Seeding CSV',
          link: 'import-seed-csv',
          disabled: options.isBracketStarted || !options.areChangesAllowed
        },
        {
          type: EMenuEntry.DIVIDER
        },
        {
          type: EMenuEntry.ITEM,
          iconClassName: 'fa fa-dice',
          className: '',
          title: t('settings:events-management.buttons.random-seed-teams'),
          link: 'seed-random',
          disabled: !options.doesTeamExist || options.isBracketStarted
        },
        {
          type: EMenuEntry.DIVIDER
        },
        {
          type: EMenuEntry.ITEM,
          iconClassName: 'fa fa-trash-xmark text-danger',
          className: '',
          title: t('settings:events-management.buttons.remove-unseeded-teams'),
          link: 'remove-unseeded',
          disabled:
            !options.doesTeamExist ||
            options.isEveryTeamSeeded ||
            options.isBracketStarted ||
            !options.areChangesAllowed
        },
        ...(options.requireTeamCheckin
          ? [
              {
                type: EMenuEntry.ITEM,
                iconClassName: 'fa fa-trash text-danger',
                className: '',
                title: t('settings:events-management.buttons.remove-unchecked-in-teams'),
                link: 'remove-unchecked-in',
                disabled: !options.isSomeTeamsUncheckedIn || options.isBracketStarted || !options.areChangesAllowed
              }
            ]
          : []),
        {
          type: EMenuEntry.DIVIDER
        },
        {
          type: EMenuEntry.ITEM,
          iconClassName: 'fa fa-trash text-danger',
          className: '',
          title: t('settings:events-management.buttons.remove-all-teams'),
          link: 'remove-all',
          disabled: !options.doesTeamExist || options.isBracketStarted || !options.areChangesAllowed
        }
      ]
    }
  ];
};
export interface IGetManageTeamMenusProps {
  isOwner: boolean;
  isMember: boolean;
}

export const getManageTeamMenus = (props: IGetManageTeamMenusProps): TMenuEntry[] => {
  return [
    {
      type: EMenuEntry.ITEM,
      title: (
        <span>
          Manage Team &nbsp; <i className="fa fa-cog"></i>
        </span>
      ),
      children: [
        ...((props.isOwner
          ? [
              {
                type: EMenuEntry.ITEM,
                title: 'Regenerate Team Invite Link',
                link: 'regen',
                className: 'text-bold',
                iconClassName: 'fa fa-fw fa-arrows-rotate'
              },
              { type: EMenuEntry.DIVIDER },
              {
                type: EMenuEntry.ITEM,
                title: 'Disband Team',
                link: 'disband',
                className: 'text-danger text-bold',
                iconClassName: 'fa fa-fw fa-circle-xmark'
              }
            ]
          : []) as TMenuEntry[]),
        ...((props.isMember
          ? [
              {
                type: EMenuEntry.ITEM,
                title: 'Leave Team',
                link: 'leave',
                className: 'text-danger text-bold',
                iconClassName: 'fa fa-fw fa-circle-xmark'
              }
            ]
          : []) as TMenuEntry[])
      ]
    }
  ];
};
